import React, { useState } from 'react';
import './AiSearch.css';
import { useConfig } from './ConfigContext';
import { transcribeAudio } from './services/apiService';
import SearchField from './SearchField';
import PlaceholderSearch from './PlaceholderSearch';
import SearchResultsContainer from './SearchResultsContainer';
import SemanticSearch, {
  createSemanticSearchContext,
  getNextSemanticSelectedFilters,
  performSemanticSearch,
  useSemanticSearchState,
} from './SemanticSearch';
import {
  createFilterSearchContext,
  performFilterSearch,
} from './FilterSearch';
import {
  createArdOnlineSearchContext,
  performArdOnlineSearch,
} from './ArdOnlineSearch';
import AiAgentSearch, {
  createAiAgentSearchContext,
  performAiAgentSearch,
} from './AiAgentSearch';
import LookUpSuggestions, {
  createLookupSearchContext,
  performLookupSearch,
} from './LookUpSearch';


const AiSearch = ({ requestConfig, keywordWeight, setError }) => {
  const config = useConfig();
  const [selectedFeature, setSelectedFeature] = useState('augment');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [sttError, setSttError] = useState(null);
  const [sttUploading, setSttUploading] = useState(false);
  const [searchContext, setSearchContext] = useState('');
  const [fromPosition, setFromPosition] = useState(0);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [originalQuery, setOriginalQuery] = useState('');
  const [data, setData] = useState([]);
  const [accordionInfo, setAccordionInfo] = useState(null);
  const [lookupSuggestions, setLookupSuggestions] = useState([]);
  const [lookupBaseQuery, setLookupBaseQuery] = useState('');

  const isSemanticMode = selectedFeature === 'augment';
  const isAgenticMode = selectedFeature === 'agentic';
  const isMainSearchLoading = isLoading && !isLoadingResults;
  const {
    intentSuggestions,
    trendingSuggestions,
    semanticFilters,
    selectedSemanticFilters,
    setSelectedSemanticFilters,
    resetSemanticState,
    loadSemanticRefinementFeatures,
  } = useSemanticSearchState({
    config,
    isSemanticMode,
    searchContext,
  });

  const getSearchContext = (value, mode = selectedFeature, correctQuery = true) => {
    if (mode === 'augment') {
      return createSemanticSearchContext(value, correctQuery);
    }

    if (mode === 'agentic') {
      return createAiAgentSearchContext(value);
    }

    if (mode === 'lookup') {
      return createLookupSearchContext(value);
    }

    return createFilterSearchContext(value);
  };

  const resetCommonState = () => {
    setSttError(null);
    setSttUploading(false);
    setFromPosition(0);
    setQuery('');
    setOriginalQuery('');
    setData([]);
    setAccordionInfo(null);
    setLookupSuggestions([]);
    setLookupBaseQuery('');
    resetSemanticState();
  };

  const handleReset = () => {
    resetCommonState();
    setInput('');
    setSearchContext('');
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleInputChange = (value) => {
    setInput(value);
    setSearchContext(getSearchContext(value));

    if (selectedFeature === 'lookup') {
      setLookupSuggestions([]);
      setLookupBaseQuery('');
    }
  };

  const handleFeatureChange = (mode) => {
    setSelectedFeature(mode);
    setInput('');
    setSearchContext('');
    resetCommonState();
  };

  const handleTranscribeAudio = async (blob) => {
    setSttError(null);
    setSttUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');

      const data = await transcribeAudio(config, formData);
      const spokenText = data.text || '';

      if (spokenText) {
        setInput((prev) => {
          const nextInput = prev ? `${prev} ${spokenText}` : spokenText;
          setSearchContext(getSearchContext(nextInput));
          return nextInput;
        });
      }
    } catch (err) {
      console.error(err);
      setSttError('Transcription failed. Please try again later');
    } finally {
      setSttUploading(false);
    }
  };

  const handleSearch = async (
    fromPos = fromPosition,
    resetState = false,
    ctxOverride = null,
    semanticFilterOverride = selectedSemanticFilters,
    resultsOnly = false
  ) => {
    if (resetState) {
      resetCommonState();
    }

    setIsLoading(true);
    setIsLoadingResults(resultsOnly);

    try {
      const ctx = ctxOverride || searchContext;
      const result = selectedFeature === 'augment'
        ? await performSemanticSearch(
          config,
          requestConfig,
          keywordWeight,
          fromPos,
          ctx,
          resetState ? {} : semanticFilterOverride
        )
        : selectedFeature === 'lookup'
          ? await performLookupSearch(
            config,
            requestConfig,
            keywordWeight,
            fromPos,
            ctx
          )
        : selectedFeature === 'agentic'
          ? await performAiAgentSearch(config, requestConfig, fromPos, ctx)
          : selectedFeature === 'ardonline'
            ? await performArdOnlineSearch(config, requestConfig, fromPos, ctx)
          : await performFilterSearch(config, requestConfig, fromPos, ctx);

      if (selectedFeature === 'augment' && fromPos === 0) {
        await loadSemanticRefinementFeatures(result.usedQuery, ctx);
      }

      if (selectedFeature === 'lookup' && !ctx?.expandedQuery) {
        setLookupSuggestions(result.suggestions || []);
        setLookupBaseQuery(result.lookupQuery || '');
      }

      setData(result.data);
      setOriginalQuery(result.originalQuery);
      setQuery(result.usedQuery);
      setAccordionInfo(result.accordionInfo);
      setIsLoading(false);
      setIsLoadingResults(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      setIsLoadingResults(false);
      setError('Failed to fetch error: ' + err.message);
    }
  };

  const handleSuggestionsSearch = async (suggestion) => {
    const nextContext = createSemanticSearchContext(suggestion, true);
    setInput(suggestion);
    setSearchContext(nextContext);
    await handleSearch(0, true, nextContext);
  };

  const handleLookupSuggestionClick = async (suggestion) => {
    const nextContext = createLookupSearchContext(lookupBaseQuery || input, suggestion);
    setFromPosition(0);
    setInput(nextContext.expandedQuery);
    setSearchContext(nextContext);
    await handleSearch(0, false, nextContext);
  };

  const handleSemanticFilterToggle = async (filter) => {
    const nextSelectedFilters = getNextSemanticSelectedFilters(selectedSemanticFilters, filter);
    setSelectedSemanticFilters(nextSelectedFilters);
    setFromPosition(0);
    await handleSearch(0, false, null, nextSelectedFilters, true);
  };

  const handleNotCorrectedAugmentSearch = async () => {
    const context = createSemanticSearchContext(originalQuery, false);
    setSearchContext(context);
    await handleSearch(0, false, context);
  };

  const handlePreviousPage = async () => {
    const newFromPos = Math.max(0, fromPosition - requestConfig.maxResults);
    setFromPosition(newFromPos);
    await handleSearch(newFromPos);
  };

  const handleNextPage = async () => {
    const newFromPos = fromPosition + requestConfig.maxResults;
    setFromPosition(newFromPos);
    await handleSearch(newFromPos);
  };

  return (
    <div className="ai-search-container">
      <div className="ai-search-panel">
        <div className="ai-search-mode-selector">
          <button
            className={selectedFeature === 'augment' ? 'ai-search-mode-btn selected' : 'ai-search-mode-btn'}
            onClick={() => handleFeatureChange('augment')}
          >
            Semantic
          </button>
          <button
            className={selectedFeature === 'lookup' ? 'ai-search-mode-btn selected' : 'ai-search-mode-btn'}
            onClick={() => handleFeatureChange('lookup')}
          >
            Look-Up
          </button>
          <button
            className={selectedFeature === 'free-text' ? 'ai-search-mode-btn selected' : 'ai-search-mode-btn'}
            onClick={() => handleFeatureChange('free-text')}
          >
            Filter
          </button>
          <button
            className={selectedFeature === 'agentic' ? 'ai-search-mode-btn selected' : 'ai-search-mode-btn'}
            onClick={() => handleFeatureChange('agentic')}
          >
            KI-Agent
          </button>
          <button
            className={selectedFeature === 'ardonline' ? 'ai-search-mode-btn selected' : 'ai-search-mode-btn'}
            onClick={() => handleFeatureChange('ardonline')}
          >
            ARDonline
          </button>
        </div>
        <SearchField
          input={input}
          onInputChange={handleInputChange}
          onSendClick={() => input && handleSearch(0, true)}
          onTranscribeAudio={handleTranscribeAudio}
          sttError={sttError}
          sttUploading={sttUploading}
        />
        {selectedFeature === 'augment' && (
          <SemanticSearch
            query={query}
            originalQuery={originalQuery}
            fromPosition={fromPosition}
            isMainSearchLoading={isMainSearchLoading}
            trendingSuggestions={trendingSuggestions}
            intentSuggestions={intentSuggestions}
            semanticFilters={semanticFilters}
            selectedSemanticFilters={selectedSemanticFilters}
            onSuggestionClick={handleSuggestionsSearch}
            onSemanticFilterToggle={handleSemanticFilterToggle}
            onNotCorrectedSearch={handleNotCorrectedAugmentSearch}
            isLoadingResults={isLoadingResults}
          />
        )}
        {selectedFeature === 'lookup' && (
          <LookUpSuggestions
            suggestions={lookupSuggestions}
            isMainSearchLoading={isMainSearchLoading}
            onSuggestionClick={handleLookupSuggestionClick}
          />
        )}
        {selectedFeature === 'agentic' && <AiAgentSearch isLoading={isLoading} />}
        {isMainSearchLoading && !isAgenticMode && <div className='loading-container'><div className="spinner"></div></div>}
      </div>
      {!isLoadingResults && query && (
        <SearchResultsContainer
          items={data}
          query={query}
          originalQuery={originalQuery}
          selectedFeature={'search'}
          aiSearchMode={selectedFeature}
          accordionInfo={accordionInfo}
          fromPosition={fromPosition}
          maxResults={requestConfig.maxResults}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          showPagination={selectedFeature !== 'agentic'}
          handleReset={handleReset}
        />
      )}
      {!isLoading && !query && <PlaceholderSearch />}
    </div>
  );
};

export default AiSearch;